import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Upload,
  X,
  Calendar,
  DollarSign,
  Ticket,
  MapPin,
  Image,
  Plus,
} from 'lucide-react';
import {
  Card,
  Button,
  ShimmerButton,
  Input,
  Textarea,
  Select,
  NumberInput,
  useToast,
  cn,
  formatNaira,
} from '@raffle/ui';

export const PropertyEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const isNew = !id;

  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    marketValue: 0,
    ticketPrice: 50000,
    totalTickets: 10000,
    drawDate: '',
    status: 'pending',
    features: [''],
    images: [] as string[],
  });

  const updateField = <K extends keyof typeof formData>(field: K, value: typeof formData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const addFeature = () => {
    setFormData((prev) => ({ ...prev, features: [...prev.features, ''] }));
  };

  const updateFeature = (index: number, value: string) => {
    setFormData((prev) => {
      const features = [...prev.features];
      features[index] = value;
      return { ...prev, features };
    });
  };

  const removeFeature = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      // In a real app, you'd upload to a server and get URLs back
      const newImages = Array.from(files).map((file) => URL.createObjectURL(file));
      setFormData((prev) => ({ ...prev, images: [...prev.images, ...newImages] }));
    }
  };

  const removeImage = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    // Validate
    if (!formData.title || !formData.location || !formData.marketValue || !formData.drawDate) {
      toast.error('Missing Information', 'Please fill in all required fields');
      setIsSaving(false);
      return;
    }

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1500));

    toast.success(
      isNew ? 'Property Created' : 'Property Updated',
      isNew ? 'Your property raffle is now live' : 'Changes have been saved'
    );
    navigate('/properties');
  };

  const totalRevenue = formData.ticketPrice * formData.totalTickets;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-vault bg-surface-2 hover:bg-surface-3 text-text-muted transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="font-display text-2xl font-bold text-text-primary">
            {isNew ? 'Add New Property' : 'Edit Property'}
          </h1>
          <p className="text-text-secondary">
            {isNew ? 'Create a new raffle listing' : 'Update property details'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <Card className="p-6">
              <h2 className="font-display text-lg font-semibold text-text-primary mb-6">
                Basic Information
              </h2>
              <div className="space-y-6">
                <Input
                  label="Property Title"
                  value={formData.title}
                  onChange={(e) => updateField('title', e.target.value)}
                  placeholder="e.g., Luxury Villa in Lekki Phase 1"
                />

                <Textarea
                  label="Description"
                  value={formData.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  placeholder="Describe the property in detail..."
                  rows={5}
                />

                <Input
                  label="Location"
                  value={formData.location}
                  onChange={(e) => updateField('location', e.target.value)}
                  placeholder="e.g., Lekki Phase 1, Lagos"
                  leftIcon={<MapPin className="w-5 h-5" />}
                />
              </div>
            </Card>

            {/* Images */}
            <Card className="p-6">
              <h2 className="font-display text-lg font-semibold text-text-primary mb-6">
                Property Images
              </h2>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {formData.images.map((image, index) => (
                  <div key={index} className="relative aspect-video rounded-vault-lg overflow-hidden group">
                    <img src={image} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    {index === 0 && (
                      <span className="absolute bottom-2 left-2 px-2 py-0.5 text-xs font-medium bg-gold-500 text-black rounded-full">
                        Cover
                      </span>
                    )}
                  </div>
                ))}

                <label className="aspect-video rounded-vault-lg border-2 border-dashed border-white/10 hover:border-gold-500/50 flex flex-col items-center justify-center cursor-pointer transition-colors">
                  <Upload className="w-8 h-8 text-text-muted mb-2" />
                  <span className="text-sm text-text-muted">Upload Image</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </label>
              </div>
              <p className="text-xs text-text-muted mt-4">
                Upload at least 3 high-quality images. First image will be used as cover.
              </p>
            </Card>

            {/* Features */}
            <Card className="p-6">
              <h2 className="font-display text-lg font-semibold text-text-primary mb-6">
                Property Features
              </h2>
              <div className="space-y-3">
                {formData.features.map((feature, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={feature}
                      onChange={(e) => updateFeature(index, e.target.value)}
                      placeholder="e.g., 5 Bedrooms, Private Pool"
                      className="flex-1"
                    />
                    <button
                      type="button"
                      onClick={() => removeFeature(index)}
                      className="p-3 rounded-vault bg-surface-2 text-text-muted hover:text-vault-error hover:bg-vault-error/10 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ))}
                <Button type="button" variant="outline" onClick={addFeature}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Feature
                </Button>
              </div>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Pricing */}
            <Card className="p-6">
              <h2 className="font-display text-lg font-semibold text-text-primary mb-6">
                Pricing & Tickets
              </h2>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Market Value
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted">₦</span>
                    <input
                      type="number"
                      value={formData.marketValue || ''}
                      onChange={(e) => updateField('marketValue', Number(e.target.value))}
                      placeholder="0"
                      className="w-full h-12 pl-8 pr-4 bg-surface-2 border border-white/10 rounded-vault font-mono text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold-500/50"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Ticket Price
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted">₦</span>
                    <input
                      type="number"
                      value={formData.ticketPrice || ''}
                      onChange={(e) => updateField('ticketPrice', Number(e.target.value))}
                      placeholder="0"
                      className="w-full h-12 pl-8 pr-4 bg-surface-2 border border-white/10 rounded-vault font-mono text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold-500/50"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Total Tickets
                  </label>
                  <input
                    type="number"
                    value={formData.totalTickets || ''}
                    onChange={(e) => updateField('totalTickets', Number(e.target.value))}
                    placeholder="10000"
                    className="w-full h-12 px-4 bg-surface-2 border border-white/10 rounded-vault font-mono text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold-500/50"
                  />
                </div>

                <div className="p-4 rounded-vault bg-gold-500/10 border border-gold-500/20">
                  <p className="text-sm text-text-muted mb-1">Potential Revenue</p>
                  <p className="font-mono text-2xl font-bold text-gold-500 tabular-nums">
                    {formatNaira(totalRevenue)}
                  </p>
                </div>
              </div>
            </Card>

            {/* Schedule */}
            <Card className="p-6">
              <h2 className="font-display text-lg font-semibold text-text-primary mb-6">
                Schedule
              </h2>
              <div className="space-y-6">
                <Input
                  label="Draw Date"
                  type="date"
                  value={formData.drawDate}
                  onChange={(e) => updateField('drawDate', e.target.value)}
                  leftIcon={<Calendar className="w-5 h-5" />}
                />

                <Select
                  label="Status"
                  value={formData.status}
                  onChange={(e) => updateField('status', e.target.value)}
                  options={[
                    { value: 'pending', label: 'Pending (Draft)' },
                    { value: 'active', label: 'Active (Live)' },
                  ]}
                />
              </div>
            </Card>

            {/* Actions */}
            <Card className="p-6">
              <div className="space-y-3">
                <ShimmerButton type="submit" fullWidth loading={isSaving}>
                  {isNew ? 'Create Property' : 'Save Changes'}
                </ShimmerButton>
                <Button type="button" variant="outline" fullWidth onClick={() => navigate(-1)}>
                  Cancel
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
};
